import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, type MouseEvent } from "react";
import { Loader2, ShieldOff } from "lucide-react";

interface BlockUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  onConfirm: () => boolean | void | Promise<boolean | void>;
}

export const BlockUserDialog = ({
  open,
  onOpenChange,
  userName,
  onConfirm,
}: BlockUserDialogProps) => {
  const [blocking, setBlocking] = useState(false);

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (blocking) return;
    setBlocking(true);
    try {
      const succeeded = await onConfirm();
      if (succeeded !== false) onOpenChange(false);
    } finally {
      setBlocking(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !blocking && onOpenChange(nextOpen)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-5 w-5 text-destructive" />
            Block {userName}?
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>If you block this user:</p>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>They won't be able to send you friend requests or messages</li>
              <li>You won't see their activity in the app</li>
              <li>Any existing friend connection will be removed</li>
              <li>They won't be notified that you blocked them</li>
            </ul>
            <p className="pt-1">You can unblock them later from their profile.</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={blocking}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={blocking}
            aria-busy={blocking || undefined}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {blocking && <Loader2 className="animate-spin" />}
            {blocking ? "Blocking…" : "Block User"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
